const normalizeRole = (role = '') => role.toLowerCase().replace(/-/g, '_');

const ROLE_CANONICAL_MAP = {
  team_admin: 'department_admin',
};

const toCanonicalRole = (role = '') => ROLE_CANONICAL_MAP[normalizeRole(role)] || normalizeRole(role);

const ROLE_ALIASES = {
  'department-admin': new Set(['department_admin', 'team_admin']),
  'tournament-admin': new Set(['tournament_admin']),
};

const getRolesToRemove = (roleToRemove) => ROLE_ALIASES[roleToRemove] || new Set([normalizeRole(roleToRemove)]);

const idsMatch = (leftId, rightId) => String(leftId || '') === String(rightId || '');

const getAdminRowId = (admin = {}) => admin.userId || admin.id || admin._id;

const getScopedRoles = (admin = {}) => {
  const roles = [...(Array.isArray(admin.roles) ? admin.roles : []), admin.role]
    .filter(Boolean)
    .map((role) => toCanonicalRole(role));

  return [...new Set(roles)];
};

export const normalizeAdminsRows = (admins = []) =>
  admins.reduce((acc, admin) => {
    const rowId = getAdminRowId(admin);
    if (!rowId) return acc;

    const existing = acc.find((row) => idsMatch(getAdminRowId(row), rowId));
    const nextRoles = getScopedRoles(admin);

    if (!existing) {
      acc.push({
        ...admin,
        userId: admin.userId || rowId,
        roles: nextRoles,
      });
      return acc;
    }

    const mergedRoles = [...new Set([...(existing.roles || []), ...nextRoles])];
    existing.roles = mergedRoles;
    return acc;
  }, []);

const pruneAdminRoles = (admin, roleToRemove) => {
  const rolesToRemove = getRolesToRemove(roleToRemove);
  const nextRoles = (admin?.roles || []).filter((role) => !rolesToRemove.has(toCanonicalRole(role)));

  const nextManagerRoles = (admin?.managerRoles || []).filter((role) => {
    const normalized = normalizeRole(String(role).replace(/\s+/g, '_'));
    return !rolesToRemove.has(normalized);
  });

  if (nextRoles.length === 0) {
    return null;
  }

  return {
    ...admin,
    roles: nextRoles,
    managerRoles: nextManagerRoles,
  };
};

export const updateAdminsAfterRoleRemoval = (admins, { adminId, userId, roleToRemove }) => {
  const targetId = userId || adminId;

  return normalizeAdminsRows(admins).reduce((acc, admin) => {
    const currentId = getAdminRowId(admin);
    if (!idsMatch(currentId, targetId) && !idsMatch(admin.userId, targetId)) {
      acc.push(admin);
      return acc;
    }

    const nextAdmin = pruneAdminRoles(admin, roleToRemove);
    if (nextAdmin) {
      acc.push(nextAdmin);
    }
    return acc;
  }, []);
};
